<template>
  <div class="container">
    <van-tabs
      v-model="activeTab"
      class="scoreboard-tabs"
      :ellipsis="false"
      scrollspy
      swipeable
      :line-width="0"
    >
      <van-tab
        v-for="(tab, index) in tabs"
        :key="index"
        :title="tab.label"
        :name="tab.value"
      />
    </van-tabs>
    <div class="scoreboard" :style="{ minHeight: bracketH + 'px' }">
      <template v-for="(col, index) in rounds">
        <!-- Round column -->
        <div :class="['each', col.type]" :key="'col-' + index + '-' + activeTab">
          <div
            v-for="(game, l) in col.data"
            :key="`round-${index}-${l}`"
            class="match-slot"
            :style="{ height: slotH(index) + 'px' }"
          >
            <div :class="['match-wrapper', col.type]">
              <div v-if="col.label" :class="['round-label', col.type]">{{ col.label }}</div>
              <div class="match-date">
                {{ formatMatchDate(game.btm) }}
              </div>
              <div class="match-card">
                <div class="team-row" v-for="(item, j) in game.tms" :key="j">
                  <div class="team-icon">
                    <img :src="item.lurl || require('@/assets/imgs/comm/team.svg')" alt="">
                  </div>
                  <div :class="['team-bar', col.type]">
                    <div class="team-name">
                      {{ item.na || $t('home.tbd') }}
                    </div>
                    <div class="team-score">
                      {{ game.placeholder ? '-' : getScore(game, j) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Connector column -->
        <div
          v-if="index < rounds.length - 1 && !col.noConnectorAfter"
          :key="'conn-' + index + '-' + activeTab"
          class="bracket-connector"
          :style="{ height: bracketH + 'px', width: CONN_W + 'px' }"
        >
          <div
            v-for="(conn, ci) in connections(index)"
            :key="ci"
            class="connector-piece"
            :style="{
              top: conn.topY + 'px',
              height: (conn.bottomY - conn.topY) + 'px',
            }"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { Format } from "@/utils/fn";
// import { getWeek } from "@/utils/time";
import { Tab, Tabs } from "vant";

// ---- Layout constants (must match CSS) ----
const DATE_H = 14 + 4;        // .match-date height + margin-bottom
const TEAM_ROW_H = 24;        // .team-row height
const CARD_GAP = 4;           // .match-card gap
const CARD_MB = 5;            // .match-card margin-bottom
const CONN_W = 9;             // connector strip width

const matchWrapperH = DATE_H + TEAM_ROW_H * 2 + CARD_GAP + CARD_MB;

export default {
  name: 'Scoreboard',
  components: {
    [Tab.name]: Tab,
    [Tabs.name]: Tabs,
  },
  props: {
    outData: {
      type: Object,
      default: () => ({
        n16: [],
        n8: [],
        n4: [],
        n2: [],
        n1: {},
      }),
    },
  },
  computed: {
    tabs() {
      return [
        { value: 'n16', label: this.$t('home.round16') },
        { value: 'n8', label: this.$t('home.round8') },
        { value: 'n4', label: this.$t('home.round4') },
        { value: 'semi', label: this.$t('home.roundSemiFinal') },
      ];
    },
    n1Array() {
      return this.outData.n1 && this.outData.n1.tms ? [this.outData.n1] : [];
    },
    n3Array() {
      return this.outData.n3 && this.outData.n3.tms ? [this.outData.n3] : [];
    },
    rounds() {
      switch (this.activeTab) {
        case 'n16':
          return [
            { data: this.fillRound(this.outData.n16 || [], 16) },
            { data: this.fillRound(this.outData.n8, 8) },
            { data: this.fillRound(this.outData.n4, 4) },
          ];
        case 'n8':
          return [
            { data: this.fillRound(this.outData.n8, 8) },
            { data: this.fillRound(this.outData.n4, 4) },
            { data: this.fillRound(this.outData.n2, 2) },
          ];
        case 'n4':
          return [
            { data: this.fillRound(this.outData.n4, 4) },
            { data: this.fillRound(this.outData.n2, 2) },
            { data: this.fillRound(this.n1Array, 1) },
          ];
        case 'semi':
          return [
            { data: this.fillRound(this.outData.n2, 2) },
            { data: this.fillRound(this.n3Array, 1), type: 'third', label: this.$t('home.rank6'), noConnectorAfter: true },
            { data: this.fillRound(this.n1Array, 1), type: 'final', label: this.$t('home.Final') },
          ];
        default:
          return [
            { data: this.fillRound(this.outData.n8, 8) },
            { data: this.fillRound(this.outData.n4, 4) },
            { data: this.fillRound(this.outData.n2, 2) },
          ];
      }
    },
    bracketH() {
      const maxMatches = Math.max(...this.rounds.map(r => r.data.length));
      return maxMatches * matchWrapperH;
    },
  },
  data() {
    return {
      activeTab: 'n16',
      CONN_W,
    };
  },
  methods: {
    Format,
    formatMatchDate(btm) {
      if (!btm) return '--/--';
      const date = new Date(btm);
      const datePart = Format(date, "MM/dd");
      // const week = getWeek(btm);
      // const weekDay = (week && (week[this.getLocal()] || week['EN'])) || '';
      const timePart = Format(date, "hh:mm");
      return `${datePart} ${timePart}`;
    },
    slotH(ri) {
      return this.bracketH / this.rounds[ri].data.length;
    },
    matchCenterY(mi, ri) {
      const h = this.slotH(ri);
      return mi * h + h / 2 + DATE_H / 2 - CARD_MB / 2;
    },
    connections(ri) {
      if (ri >= this.rounds.length - 1) return [];
      return this.rounds[ri + 1].data.map((_, ni) => ({
        topY: this.matchCenterY(ni * 2, ri),
        bottomY: this.matchCenterY(ni * 2 + 1, ri),
      }));
    },
    fillRound(data, expectedCount) {
      const result = [...(data || [])];
      while (result.length < expectedCount) {
        result.push({
          placeholder: true,
          tms: [
            { na: '', placeholder: true },
            { na: '', placeholder: true },
          ],
        });
      }
      return result;
    },
    getScore(game, i) {
      let scoreData = (
        (game.sgs || []).find((item) => item.tyg == 5) || {}
      ).sc || ['-', '-'];

      const score = (
        game.sgs?.reduce(
          (prev, item) => {
            if ((item.pe == 1006 || item.pe == 1013) && item.tyg == 5) {
              prev = prev.length > 0 ? prev : [0, 0];
              prev[0] = prev[0] + item.sc[0];
              prev[1] = prev[1] + item.sc[1];
            }
            return prev;
          },
          [...scoreData]
        ) || scoreData
      )[i]

      return (score || score === 0)? score : '-'
    },
  },
};
</script>

<style lang="scss" scoped>

.scoreboard {
  display: flex;
  align-items: flex-start;
  width: 95.73vw;
  padding: 10px;
  margin: 0 auto;
  border-radius: 8px;
  background: var(--color-10);
  margin-top: 8px;
  margin-bottom: 80px;
  // padding-bottom: 80px;

  .each {
    flex: 1;
    min-width: 0;
  }
}

// ---- Match slot ----
.match-slot {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  padding: 0 4px;
}

.match-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
}

// ---- Round label (季军赛 / 决赛) ----
.round-label {
  display: inline-block;
  align-self: center;
  padding: 0 10px;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  white-space: nowrap;
  background: var(--color-5);
  color: var(--text-dark-color);
  border-radius: 100px;

  &.final {
    background: #F80000;
    color: #fff;
  }
}

// ---- Centered layout for 季军赛 / 决赛 ----
.match-wrapper.third,
.match-wrapper.final {
  .match-date {
    text-align: center;
  }
}

// ---- Date header — height must match DATE_H constant ----
.match-date {
  height: 14px;
  margin-bottom: 4px;
  font-size: 10px;
  font-weight: 500;
  line-height: 14px;
  color: var(--text-dark-color);
  white-space: nowrap;
}

// ---- Match card — two team rows ----
.match-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 5px;
}

.team-row {
  display: flex;
  gap: 3px;
  align-items: center;
  height: 24px;
}

.team-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;

  img {
    width: 100%;
    height: 100%;
  }
}

.team-bar {
  display: flex;
  flex: 1;
  height: 100%;
  overflow: hidden;
  background: #0A2ACF;

  &.final {
    background: #F80000;

    .team-score {
      background: #000;
    }
  }
}

.team-name {
  flex: 1;
  padding: 0 4px;
  overflow: hidden;
  font-size: 12px;
  line-height: 24px;
  color: #fff;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.team-score {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 16px;
  font-size: 11px;
  color: #fff;
  background: #3757F7;
}

// ---- Connector column ----
.bracket-connector {
  position: relative;
  flex-shrink: 0;
}

.connector-piece {
  position: absolute;
  right: 0;
  left: 0;
  border-top: 1px solid #304FFF;
  border-right: 1px solid #304FFF;
  border-bottom: 1px solid #304FFF;
}

:deep(.van-tabs__nav--line) {
  padding-bottom: 0;
  padding-left: 0!important;
}
</style>

<style lang="scss">
.scoreboard-tabs {
  padding: 8px 8px 0;

  .van-tabs__wrap {
    border: none;
    height: auto;
  }

  .van-tabs__nav {
    background: transparent;
    gap: 4px;
  }

  .van-tab {
    flex: none;
    padding: 8px 12px;
    border-radius: 100px;
    font-size: 12px;
    font-weight: 600;
    background: var(--color-10);
    color: var(--color-2);
    white-space: nowrap;
    line-height: 1;
    min-width: auto;
  }

  .van-tab--active {
    background: #F80000;
    color: #fff;
  }

  .van-tabs__line {
    display: none;
  }
}
</style>
